import React, { useMemo, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const Contact = () => {
  const { t } = useLanguage();
  const businessTypeOptions = t("contact.businessTypeOptions", []);
  const solutionOptions = t("contact.solutionOptions", []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const englishBusinessTypeOptions = translations.en.contact.businessTypeOptions;
  const englishSolutionOptions = translations.en.contact.solutionOptions;

  const businessTypeToEnglish = useMemo(() => {
    const map = new Map();
    businessTypeOptions.forEach((option, index) => {
      map.set(option, englishBusinessTypeOptions[index] || option);
    });
    return map;
  }, [businessTypeOptions, englishBusinessTypeOptions]);

  const solutionToEnglish = useMemo(() => {
    const map = new Map();
    solutionOptions.forEach((option, index) => {
      map.set(option, englishSolutionOptions[index] || option);
    });
    return map;
  }, [solutionOptions, englishSolutionOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    try {
      const formData = new FormData(form);
      const selectedBusinessType = formData.get("business_type");
      const selectedSolutionInterest = formData.get("solution_interest");
      const payload = {
        firstName: formData.get("first_name"),
        lastName: formData.get("last_name"),
        email: formData.get("email"),
        companyWebsite: formData.get("company_website"),
        businessType:
          businessTypeToEnglish.get(selectedBusinessType) || selectedBusinessType,
        solutionInterest:
          solutionToEnglish.get(selectedSolutionInterest) || selectedSolutionInterest,
        message: formData.get("message"),
      };

      const res = await fetch(
        "https://hook.eu2.make.com/yav71zrw221x24svqi4cbqtv9e2ltu2d",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      setSubmitStatus("success");
      setSubmitMessage(t("contact.submitSuccess"));
      form.reset();
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage(t("contact.submitError"));
      console.error("Contact form submit failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-[65px] lg:mt-[80px] min-h-screen pb-20 md:pb-10 bg-white">
      <div className="mx-auto sm:max-w-3xl lg:max-w-screen-2xl 2xl:max-w-screen-2xl px-5 md:px-8">
        <div className="w-full text-center pt-16 pb-4">
          <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-orange mb-3">
            {t("contact.label")}
          </p>
          <h1 className="text-blue text-3xl md:text-4xl lg:text-5xl font-bold">
            {t("contact.title")}
          </h1>
          <div className="mt-4 mx-auto w-20 h-1 rounded-full bg-gradient-to-r from-orange to-pink" />
        </div>

        <div className="md:flex w-full gap-10 lg:gap-16 mt-10 lg:mt-16 items-start">
          <div className="w-full md:w-5/12 space-y-4">
            <div className="rounded-2xl border border-blue/10 bg-gradient-to-br from-white to-gray-50/50 p-6 shadow-sm shadow-blue/5 transition-all duration-300 hover:shadow-md hover:border-blue/20">
              <h2 className="text-lg font-bold text-orange flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-orange/10 to-pink/10 text-orange text-sm">
                  📍
                </span>
                {t("contact.address")}
              </h2>
              <h3 className="text-blue text-base lg:text-lg font-bold mt-3">
                {t("contact.officeName")}
              </h3>
              <p className="text-darkBlue/70 text-sm lg:text-base leading-relaxed mt-2">
                {t("contact.addressDetails")}
              </p>
            </div>

            <div className="rounded-2xl border border-blue/10 bg-gradient-to-br from-white to-gray-50/50 p-6 shadow-sm shadow-blue/5 transition-all duration-300 hover:shadow-md hover:border-blue/20">
              <h2 className="text-lg font-bold text-orange flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-orange/10 to-pink/10 text-orange text-sm">
                  📞
                </span>
                {t("contact.phone")}
              </h2>
              <a
                href="tel:020969145"
                className="text-darkBlue/70 text-sm lg:text-base mt-2 block hover:text-orange transition-colors duration-200"
              >
                Tel. 02 096 9145
              </a>
            </div>

            <div className="rounded-2xl border border-blue/10 bg-gradient-to-br from-white to-gray-50/50 p-6 shadow-sm shadow-blue/5 transition-all duration-300 hover:shadow-md hover:border-blue/20">
              <h2 className="text-lg font-bold text-orange flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-orange/10 to-pink/10 text-orange text-sm">
                  ✉️
                </span>
                {t("contact.email")}
              </h2>
              <a className="text-darkBlue/70 text-sm lg:text-base mt-2 block  transition-colors duration-200">
                <b> {t("contact.general_inquiries")}:</b> biz.support@ipps.co.th
              </a>
              <a className="text-darkBlue/70 text-sm lg:text-base mt-2 block  transition-colors duration-200">
                <b>{t("contact.customer_service")}:</b>{" "}
                customer.service@ipps.co.th (
                {t("contact.for_existing_customer")})
              </a>
              <a className="text-darkBlue/70 text-sm lg:text-base mt-2 block  transition-colors duration-200">
                <b>{t("contact.job_applications")}:</b> biz.support@ipps.co.th
              </a>
            </div>

            <div className="rounded-2xl border border-blue/10 bg-gradient-to-br from-white to-gray-50/50 p-6 shadow-sm shadow-blue/5 transition-all duration-300 hover:shadow-md hover:border-blue/20">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1786.5409950113783!2d100.55713286942395!3d13.806882736893547!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e29d53af97f161%3A0xf66d65ee3fc27967!2sS-OASIS!5e1!3m2!1sen!2sth!4v1771554389450!5m2!1sen!2sth"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className=" w-full h-48"
              ></iframe>
            </div>
          </div>

          <div className="w-full md:w-7/12 mt-10 md:mt-0">
            <div className="rounded-2xl border border-blue/10 bg-white p-6 lg:p-10 shadow-md shadow-blue/10">
              <p className="text-sm font-semibold uppercase tracking-widest text-darkBlue/50">
                {t("contact.formLabel")}
              </p>
              <h2 className="text-2xl lg:text-3xl font-bold text-blue mt-1">
                {t("common.writeMessage")}
              </h2>
              <div className="mt-2 w-16 h-1 rounded-full bg-gradient-to-r from-orange to-pink" />

              <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                <div className="flex gap-4">
                  <div className="w-full">
                    <label
                      htmlFor="first_name"
                      className="block mb-2 text-sm font-medium text-darkBlue/80"
                    >
                      {t("contact.firstName")}
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none placeholder:text-darkBlue/30"
                      placeholder={t("contact.firstNamePlaceholder")}
                      required
                    />
                  </div>
                  <div className="w-full">
                    <label
                      htmlFor="last_name"
                      className="block mb-2 text-sm font-medium text-darkBlue/80"
                    >
                      {t("contact.lastName")}
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none placeholder:text-darkBlue/30"
                      placeholder={t("contact.lastNamePlaceholder")}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block mb-2 text-sm font-medium text-darkBlue/80"
                  >
                    {t("contact.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none placeholder:text-darkBlue/30"
                    placeholder={t("contact.emailPlaceholder")}
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="company_website"
                    className="block mb-2 text-sm font-medium text-darkBlue/80"
                  >
                    {t("contact.companyWebsite")}
                  </label>
                  <input
                    type="text"
                    id="company_website"
                    name="company_website"
                    className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none placeholder:text-darkBlue/30"
                    placeholder={t("contact.companyWebsitePlaceholder")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="business_type"
                    className="block mb-2 text-sm font-medium text-darkBlue/80"
                  >
                    {t("contact.businessTypes")}
                  </label>
                  <select
                    id="business_type"
                    name="business_type"
                    className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none"
                    required
                    defaultValue=""
                  >
                    <option value="" disabled>
                      {t("contact.selectBusinessType")}
                    </option>
                    {businessTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="solution_interest"
                    className="block mb-2 text-sm font-medium text-darkBlue/80"
                  >
                    {t("contact.solutionInterest")}
                  </label>
                  <select
                    id="solution_interest"
                    name="solution_interest"
                    className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none"
                    required
                    defaultValue=""
                  >
                    <option value="" disabled>
                      {t("contact.selectSolution")}
                    </option>
                    {solutionOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block mb-2 text-sm font-medium text-darkBlue/80"
                  >
                    {t("contact.message")}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    className="bg-gray-50 border border-blue/20 text-darkBlue text-sm rounded-xl focus:ring-2 focus:ring-orange/30 focus:border-orange block w-full p-3 transition-all duration-200 outline-none resize-none placeholder:text-darkBlue/30"
                    placeholder={t("contact.messagePlaceholder")}
                    required
                  />
                </div>

                {submitStatus !== "idle" && (
                  <p
                    className={`text-sm ${submitStatus === "success" ? "text-green-600" : "text-red-600"}`}
                  >
                    {submitMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange text-white font-bold text-base py-3 px-6 rounded-xl transition-all duration-300 hover:bg-pink hover:shadow-lg hover:-translate-y-0.5"
                >
                  {isSubmitting
                    ? t("contact.sending")
                    : t("common.sendMessage")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
